// Deterministic client-side presentation for a campaign.
//
// The prototype carried `paletteIndex`, `hueShift`, and `rune` directly on
// each campaign fixture. The Convex backend does not store presentation,
// so we derive these from the campaignId hash. A given campaign always
// renders the same way; two adjacent campaigns get visibly different
// shades because the hash decorrelates short-distance IDs.

const RUNE_NAMES = ['astral', 'tech', 'graph'];
const ICON_NAMES = ['astral', 'tech', 'graph'];

const hashString = (value) => {
  // 32-bit FNV-1a — small, deterministic, no deps.
  let hash = 0x811c9dc5;
  const str = String(value || '');
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
};

export const paletteIndexFor = (campaignId) => hashString(campaignId) % 5;

// Hue shifts stay within ±35° of the theme accent so every campaign reads
// as a member of the same color family. The original ±72° buckets meant a
// purple-themed app could spit out olive/yellow campaign cards once the
// rotation crossed the green range — the prototype's hand-picked campaigns
// (0°, ±25°) were a much better reference for what "tasteful variation"
// looks like. Five slots keep adjacent campaigns visibly distinct.
const HUE_SHIFT_BUCKETS = [-30, -15, 0, 15, 30];
export const hueShiftFor = (campaignId) => {
  const h = hashString(campaignId);
  return HUE_SHIFT_BUCKETS[(h >>> 3) % HUE_SHIFT_BUCKETS.length];
};

export const runeFor = (campaignId) => {
  const h = hashString(campaignId);
  return RUNE_NAMES[(h >>> 5) % RUNE_NAMES.length];
};

export const iconFor = (campaignId) => {
  const h = hashString(campaignId);
  return ICON_NAMES[(h >>> 5) % ICON_NAMES.length];
};

// Convenience: attach all four derivations to a campaign object so the
// view layer can read `camp.paletteIndex` / `camp.rune` / etc. without
// each card recomputing.
export const withPresentation = (camp) => ({
  ...camp,
  paletteIndex: paletteIndexFor(camp.campaignId || camp.id),
  hueShift: hueShiftFor(camp.campaignId || camp.id),
  rune: runeFor(camp.campaignId || camp.id),
  icon: iconFor(camp.campaignId || camp.id),
});
